import { ObservableInputsManager } from '@/shared/utils/angular-sdk';
import { type ColorPaletteConfiguration, UiColorPickerComponent } from '@/ui/color-picker/ui-color-picker.component';
import { UiFieldComponent } from '@/ui/field/ui-field';
import { UiFormFieldComponent } from '@/ui/form-field/form-field/ui-form-field';
import { UiIconButtonToggleComponent, type UiIconButtonToggleOption } from '@/ui/icon-button-toggle/ui-icon-button-toggle';
import { IconName } from '@/ui/icon/ui-icon';
import { UiSelectComponent } from '@/ui/select/ui-select';
import { UiToggleComponent } from '@/ui/toggle/ui-toggle';
import { observeControlValue$ } from '@/utils/angular/forms/reactive-forms/reactive-forms-rxjs-interop.utils';
import { ControlValueAccessorInteropDirective } from '@/utils/angular/forms/reactive-forms/reactive-forms-signals-interop.utils';
import { createArrayValidator } from '@/utils/angular/forms/reactive-forms/validators/validators.helpers';
import { ApplyPureFunctionPipe } from '@/utils/angular/pipes/apply-pure-function.pipe';
import { doNotSort } from '@/utils/toolkit/array/sort.utils';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, type OnChanges, type OnInit, Output, type SimpleChanges } from '@angular/core';
import {
    FormsModule,
    ReactiveFormsModule,
    type UntypedFormArray,
    UntypedFormBuilder,
    type UntypedFormGroup,
    type ValidatorFn,
    Validators
} from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { isEqual } from 'lodash-es';
import { combineLatest, type Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, withLatestFrom } from 'rxjs/operators';
import { canMeasuresBeSegmented } from '../../../../providers/business-intelligence/business-intelligence.utils';
import { FormErrorComponent } from '../../../form-error/form-error';
import { PathListComponent } from '../../../path-list/path-list';
import { PathListItemComponent } from '../../../path-list/path-list-item/path-list-item';
import { BusinessIntelligenceRepresentationConfigEditorComponent } from '../business-intelligence-representation-config-editor.class';
import {
    type BusinessIntelligenceDimension,
    type BusinessIntelligenceDimensionCode,
    type BusinessIntelligenceMeasure,
    type BusinessIntelligenceMeasureCode,
    BusinessIntelligenceTemporalDimensionStep
} from './../../../../interfaces/BusinessIntelligence.interface';
import {
    BusinessIntelligenceComboChartMeasureChartType,
    type BusinessIntelligenceLinesChartRepresentationConfig
} from './../../../../interfaces/BusinessIntelligenceRepresentation.interface';
import { FormattableType } from './../../../../interfaces/Formatter.interface';
import type { BusinessIntelligenceDimensionConfiguration } from './../../../../interfaces/TailoredWidget.interface';
import { BusinessIntelligenceProvider } from './../../../../providers/business-intelligence/business-intelligence.provider';

interface FormType {
    measures: {
        measureCode: BusinessIntelligenceMeasureCode;
        isCumulative: boolean;
        chartType: BusinessIntelligenceComboChartMeasureChartType;
        columnStackingGroup: number | null;
        measureColor: ColorPaletteConfiguration;
    }[];
    xAxisDimensionCode: BusinessIntelligenceDimensionCode;
    xAxisStepCode: BusinessIntelligenceTemporalDimensionStep;
    startAt: number;
    segmentationDimensionCode: BusinessIntelligenceDimensionCode;
}

@UntilDestroy()
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./business-intelligence-line-chart-representation-config-editor.scss'],
    selector: 'business-intelligence-line-chart-representation-config-editor',
    templateUrl: 'business-intelligence-line-chart-representation-config-editor.html',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        FormErrorComponent,
        PathListComponent,
        PathListItemComponent,
        UiFieldComponent,
        UiSelectComponent,
        UiIconButtonToggleComponent,
        UiToggleComponent,
        UiFormFieldComponent,
        AsyncPipe,
        TranslatePipe,
        ApplyPureFunctionPipe,
        UiColorPickerComponent,
        ControlValueAccessorInteropDirective
    ]
})
export class BusinessIntelligenceLineChartRepresentationConfigEditorComponent
    extends BusinessIntelligenceRepresentationConfigEditorComponent<BusinessIntelligenceLinesChartRepresentationConfig>
    implements OnInit, OnChanges
{
    private _businessIntelligenceProvider = inject(BusinessIntelligenceProvider);
    private _fb = inject(UntypedFormBuilder);
    private _translateService = inject(TranslateService);

    private _observableInputsManager = new ObservableInputsManager<BusinessIntelligenceLineChartRepresentationConfigEditorComponent>(this);

    @Input() config!: BusinessIntelligenceLinesChartRepresentationConfig;
    config$ = this._observableInputsManager.observe('config');

    @Input() availableMeasures: BusinessIntelligenceMeasure[] = [];
    availableMeasures$ = this._observableInputsManager.observe('availableMeasures');

    @Input() availableDimensions: BusinessIntelligenceDimension[] = [];
    availableDimensions$ = this._observableInputsManager.observe('availableDimensions');

    @Input() disabled!: boolean;

    @Output() configChanged: EventEmitter<BusinessIntelligenceLinesChartRepresentationConfig> = new EventEmitter();

    formGroup!: UntypedFormGroup;
    segmentationDimensions$!: Observable<BusinessIntelligenceDimension[]>;
    isXAxisTemporalDimension$!: Observable<boolean>;
    isXAxisContinuousDimension$!: Observable<boolean>;
    isMeasuresSegmentable$!: Observable<boolean>;
    currentStep$!: Observable<BusinessIntelligenceTemporalDimensionStep>;
    stepList: { value: BusinessIntelligenceTemporalDimensionStep; label: string }[] = [];

    hasAtLeastOneColumn$!: Observable<boolean>;

    months: number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

    stackingGroupIds = [1, 2, 3, 4];

    defaultColor: ColorPaletteConfiguration = {
        paletteName: 'cyan',
        variant: '9'
    };

    doNotSort = doNotSort;

    chartTypeOptions: UiIconButtonToggleOption[] = [
        {
            code: BusinessIntelligenceComboChartMeasureChartType.LINE,
            iconName: IconName.LINE_CHART
        },
        {
            code: BusinessIntelligenceComboChartMeasureChartType.COLUMN,
            iconName: IconName.COLUMN_CHART
        }
    ];

    ngOnInit() {
        this.stepList = Object.values(BusinessIntelligenceTemporalDimensionStep).map(step => {
            return {
                value: step,
                label: this._translateService.instant('STEPS.' + step.toUpperCase())
            };
        });

        this.segmentationDimensions$ = this.availableDimensions$.pipe(
            filter(isNotNullish),
            map(dimensions => dimensions.filter(dimension => !this._businessIntelligenceProvider.isContinuousDimension(dimension)))
        );

        this.formGroup = this._fb.group({
            measures: this._fb.array([], [this.requireSameUnitValidator(this.availableMeasures)]),
            xAxisDimensionCode: [null],
            xAxisStepCode: [null],
            startAt: [null],
            segmentationDimensionCode: [null, Validators.required]
        });

        this.config$
            .pipe(withLatestFrom(this.availableMeasures$.pipe(distinctUntilChanged())), untilDestroyed(this))
            .subscribe(([config, availableMeasures]) => {
                if (isEqual(this.generateConfigFromForm(this.formGroup.value, availableMeasures), config)) {
                    return;
                }
                if (!config || !config.measures || !config.measures.length) {
                    this.clearAllMeasures(false);
                    this.addMeasure(false);
                    this.formGroup.patchValue(
                        {
                            xAxisDimensionCode: null,
                            xAxisStepCode: null,
                            segmentationDimensionCode: null
                        },
                        { emitEvent: false }
                    );
                } else {
                    this.formGroup.setControl(
                        'measures',
                        this._fb.array(
                            config.measures.map(measure =>
                                this._fb.group({
                                    measureCode: measure.measureCode,
                                    isCumulative: measure.isCumulative,
                                    chartType: measure.chartType,
                                    columnStackingGroup: measure.columnStackingGroup,
                                    measureColor: measure.color ?? this.defaultColor
                                })
                            ),
                            [this.requireSameUnitValidator(this.availableMeasures)]
                        ),
                        { emitEvent: false }
                    );
                    this.formGroup.patchValue(
                        {
                            xAxisDimensionCode: config.xAxis ? config.xAxis.dimensionCode : null,
                            xAxisStepCode:
                                config.xAxis && config.xAxis.groupingParams && config.xAxis.groupingParams.granularity
                                    ? config.xAxis.groupingParams.granularity
                                    : null,
                            startAt: config.xAxis.groupingParams?.startAt ?? null,
                            segmentationDimensionCode: config.segmentation ? config.segmentation.dimensionCode : null
                        },
                        { emitEvent: false }
                    );
                }
            });

        this.availableMeasures$.pipe(untilDestroyed(this)).subscribe(availableMeasures => {
            this.formGroup.controls.measures.setValidators([this.requireSameUnitValidator(availableMeasures), Validators.required]);
        });

        const xAxisDimension$ = combineLatest([
            this.availableDimensions$.pipe(filter(isNotNullish)),
            observeControlValue$(this.formGroup.controls.xAxisDimensionCode)
        ]).pipe(map(([dimensionList, dimensionCode]) => dimensionList.find(dimension => dimension.dimensionCode === dimensionCode)));

        this.isXAxisTemporalDimension$ = xAxisDimension$.pipe(
            map(dimension => this._businessIntelligenceProvider.isTemporalDimension(dimension))
        );

        this.isXAxisContinuousDimension$ = xAxisDimension$.pipe(
            map(dimension => this._businessIntelligenceProvider.isContinuousDimension(dimension))
        );

        this.currentStep$ = observeControlValue$(this.formGroup.controls.xAxisStepCode);

        this.formGroup.valueChanges
            .pipe(withLatestFrom(this.availableMeasures$.pipe(distinctUntilChanged())), untilDestroyed(this))
            .subscribe(([formValue, availableMeasures]) => {
                this.configChanged.emit(this.generateConfigFromForm(formValue, availableMeasures));
            });

        this.hasAtLeastOneColumn$ = (
            observeControlValue$(this.formGroup.controls.measures) as Observable<BusinessIntelligenceMeasure[]>
        ).pipe(map(measures => measures.some((measure: any) => measure.chartType === 'column')));

        this.isMeasuresSegmentable$ = combineLatest([
            this.availableMeasures$.pipe(filter(isNotNullish)),
            observeControlValue$(this.formGroup.controls.measures) as Observable<BusinessIntelligenceMeasure[]>
        ]).pipe(
            map(([availableMeasures, measures]) =>
                canMeasuresBeSegmented(
                    availableMeasures,
                    measures
                        .filter(measure => isNotNullish(measure) && isNotNullish(measure.measureCode))
                        .map(measure => measure.measureCode)
                )
            )
        );
    }

    ngOnChanges(changes: SimpleChanges) {
        this._observableInputsManager.triggerChanges(changes);
    }

    private generateConfigFromForm(
        formValue: FormType,
        availableMeasures: BusinessIntelligenceMeasure[]
    ): BusinessIntelligenceLinesChartRepresentationConfig {
        const areMeasureSegmentable = canMeasuresBeSegmented(
            availableMeasures,
            formValue.measures
                .filter(measure => isNotNullish(measure) && isNotNullish(measure.measureCode))
                .map(measure => measure.measureCode)
        );
        const segmentationDimension: BusinessIntelligenceDimensionConfiguration | null =
            formValue.segmentationDimensionCode && areMeasureSegmentable
                ? {
                      dimensionCode: formValue.segmentationDimensionCode
                  }
                : null;

        const xAxisDimension = this.availableDimensions.find(dim => dim.dimensionCode === formValue.xAxisDimensionCode);

        const xAxisDimensionConfiguration: BusinessIntelligenceDimensionConfiguration | null = formValue.xAxisDimensionCode
            ? {
                  dimensionCode: formValue.xAxisDimensionCode,
                  groupingParams:
                      formValue.xAxisStepCode && this._businessIntelligenceProvider.isTemporalDimension(xAxisDimension)
                          ? formValue.startAt && this.isStartAtAvailable(formValue.xAxisStepCode)
                              ? {
                                    granularity: formValue.xAxisStepCode,
                                    startAt: formValue.startAt
                                }
                              : {
                                    granularity: formValue.xAxisStepCode
                                }
                          : null
              }
            : null;

        return {
            xAxis: xAxisDimensionConfiguration!,
            segmentation: segmentationDimension,
            measures: formValue.measures
                .filter(measure => measure.measureCode)
                .map(measure => {
                    return {
                        measureCode: measure.measureCode,
                        isCumulative:
                            measure.isCumulative &&
                            this.canMeasureBeCumulated(
                                measure.measureCode,
                                this.availableMeasures,
                                this._businessIntelligenceProvider.isContinuousDimension(xAxisDimension)
                            ),
                        chartType: measure.chartType,
                        columnStackingGroup: measure.columnStackingGroup,
                        color: measure.measureColor
                    };
                }),
            displayZeros: null
        };
    }

    addMeasure(emitEvent: boolean) {
        const newMeasureControl = this._fb.group({
            measureCode: null,
            isCumulative: false,
            chartType: BusinessIntelligenceComboChartMeasureChartType.LINE,
            columnStackingGroup: null,
            measureColor: this.defaultColor
        });
        (this.formGroup.controls.measures as UntypedFormArray).push(newMeasureControl, { emitEvent });
    }

    removeMeasure(index: number) {
        (this.formGroup.controls.measures as UntypedFormArray).removeAt(index);
    }

    clearAllMeasures(emitEvent: boolean) {
        const newMeasuresArray = this._fb.array([], this.requireSameUnitValidator(this.availableMeasures));
        this.formGroup.setControl('measures', newMeasuresArray, { emitEvent });
    }

    setChartType(measureControl: UntypedFormGroup, newChartType: BusinessIntelligenceComboChartMeasureChartType) {
        switch (newChartType) {
            case BusinessIntelligenceComboChartMeasureChartType.LINE: {
                measureControl.patchValue({ chartType: newChartType, columnStackingGroup: null });
                break;
            }
            case BusinessIntelligenceComboChartMeasureChartType.COLUMN: {
                const usedStackingGroups = this.formGroup.value.measures.map((measure: any) => measure.columnStackingGroup);
                const firstAvailableGroup = this.stackingGroupIds.filter(stackGroupId => !usedStackingGroups.includes(stackGroupId))[0];
                measureControl.patchValue({
                    chartType: newChartType,
                    columnStackingGroup: firstAvailableGroup
                });
                break;
            }
            default:
                throw new Error(`Unknown chart type: "${newChartType}"`);
        }
    }

    private requireSameUnitValidator(availableMeasures: BusinessIntelligenceMeasure[]): ValidatorFn {
        if (!availableMeasures) {
            return () => null;
        } else {
            return createArrayValidator((formArray: UntypedFormArray) => {
                const controlsMeasureCodes: BusinessIntelligenceMeasureCode[] = formArray.value
                    .map((controlValue: any) => controlValue.measureCode)
                    .filter(isNotNullish);

                if (controlsMeasureCodes.length < 2) {
                    return null;
                }

                const firstMeasure = availableMeasures.find(mesure => mesure.measureCode === controlsMeasureCodes[0])!;

                for (const controlMeasureCode of controlsMeasureCodes) {
                    const currentMeasure = availableMeasures.find(mesure => mesure.measureCode === controlMeasureCode);
                    if (currentMeasure?.typeConfig.type !== firstMeasure.typeConfig.type) {
                        return { differentUnits: true };
                    }
                }
                if (firstMeasure.typeConfig.type === FormattableType.VALUE_WITH_UNIT) {
                    const firstUnitCode = firstMeasure.typeConfig.typeParams.unitCode;
                    for (const controlMeasureCode of controlsMeasureCodes) {
                        const currentMeasure = availableMeasures.find(mesure => mesure.measureCode === controlMeasureCode);
                        if ((currentMeasure?.typeConfig.typeParams as any).unitCode !== firstUnitCode) {
                            return { differentUnits: true };
                        }
                    }
                }
                return null;
            });
        }
    }

    canMeasureBeCumulated$ = (measureCode: BusinessIntelligenceMeasureCode): Observable<boolean> => {
        return combineLatest([this.availableMeasures$, this.isXAxisContinuousDimension$]).pipe(
            map(([availableMeasures, isXAxisContinuousDimension]) =>
                this.canMeasureBeCumulated(measureCode, availableMeasures, isXAxisContinuousDimension)
            )
        );
    };

    private canMeasureBeCumulated(
        measureCode: BusinessIntelligenceMeasureCode,
        measures: BusinessIntelligenceMeasure[],
        isXAxisContinuousDimension: boolean
    ): boolean {
        const measure = measures.find(m => m.measureCode === measureCode);
        return (measure ? measure.isExtensive : false) && isXAxisContinuousDimension;
    }

    isStartAtAvailable(step: BusinessIntelligenceTemporalDimensionStep | null) {
        return step === 'year';
    }
}
